import React, { useEffect, useState } from 'react'
import axios from 'axios'
// @ts-ignore
import Modal from '../../shared/modal'
import LevelBadge from '../shared/LevelBadge'
import { LocalizationContextType } from '../../shared/contexts'
import { formatTenantDateTime } from '../../../utils/formatTenantDateTime'
import { TIME_SOURCE, timeSourceTitle } from '../../../utils/timeSource'
import { admin_alert_automation_audit_log_path, queued_order_path } from '../../../../routes'
import { CHANNELS, channelDelivery, deliveryDescription, nobodyNotified } from './deliveryStatus'
import { ExecutionDetails, ExecutionRow, Occurrence } from './types'

interface ExecutionDetailsModalProps {
  execution: ExecutionRow
  localizationContext?: LocalizationContextType
  canViewMeals: boolean
  onClose: () => void
}

interface OccurrenceItemProps {
  occurrence: Occurrence
  localizationContext?: LocalizationContextType
  canViewMeals: boolean
}

const OccurrenceItem = ({ occurrence, localizationContext, canViewMeals }: OccurrenceItemProps) => (
  <li className="list-group-item">
    <div className="d-flex justify-content-between align-items-center mb-1">
      <span className="small text-muted" title={timeSourceTitle(occurrence.sent_at, TIME_SOURCE.app)}>
        {formatTenantDateTime(occurrence.sent_at, localizationContext)}
      </span>
      <LevelBadge level={occurrence.level} />
    </div>
    <div className="mb-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{occurrence.message}</div>
    {(occurrence.details.length > 0 || occurrence.queued_order_id) && (
      <dl className="row small mb-0">
        {occurrence.details.map(({ label, value }, index) => (
          <React.Fragment key={`${label}-${index}`}>
            <dt className="col-sm-4">{label}</dt>
            <dd className="col-sm-8 mb-0">{value}</dd>
          </React.Fragment>
        ))}
        {occurrence.queued_order_id && (
          <>
            <dt className="col-sm-4">Meal</dt>
            <dd className="col-sm-8 mb-0">
              {canViewMeals
                ? <a href={queued_order_path(occurrence.queued_order_id)}>#{occurrence.queued_order_id}</a>
                : `#${occurrence.queued_order_id}`}
            </dd>
          </>
        )}
      </dl>
    )}
  </li>
)

// The row already carries who was notified; what it does not carry - the occurrences behind the
// firing - is fetched on open, because one batch can hold hundreds of them.
const ExecutionDetailsModal = ({ execution, localizationContext, canViewMeals, onClose }: ExecutionDetailsModalProps) => {
  const [details, setDetails] = useState<ExecutionDetails | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    axios
      .get<ExecutionDetails>(admin_alert_automation_audit_log_path(execution.id), {
        headers: { Accept: 'application/json' }
      })
      .then((response) => setDetails(response.data))
      .catch(() => setError('Could not load the details of this execution.'))
  }, [execution.id])

  const renderOccurrences = () => {
    if (error) return <div className="text-danger small">{error}</div>
    if (!details) return <div className="text-muted small">Loading…</div>

    return (
      <>
        {details.occurrences.length > 0 && (
          <ul className="list-group">
            {details.occurrences.map((occurrence) => (
              <OccurrenceItem
                key={occurrence.id}
                occurrence={occurrence}
                localizationContext={localizationContext}
                canViewMeals={canViewMeals}
              />
            ))}
          </ul>
        )}
        {details.missing_occurrences_count > 0 && (
          <div className="text-muted small mt-2">
            {details.missing_occurrences_count} occurrence(s) are no longer available.
          </div>
        )}
      </>
    )
  }

  return (
    <Modal headerText={execution.scenario_name} onClose={onClose} widen={true}>
      <div className="mb-3">
        <strong>Fired at:</strong>{' '}
        <span title={timeSourceTitle(execution.fired_at, TIME_SOURCE.app)}>
          {formatTenantDateTime(execution.fired_at, localizationContext)}
        </span>
        {execution.scenario_deleted && <span className="text-muted small ms-2">(scenario since deleted)</span>}
      </div>

      <div className="mb-3">
        <strong>Error:</strong>{' '}
        <code className="fw-bold">{execution.business_error_code}</code>{' '}
        {execution.business_error_level && <LevelBadge level={execution.business_error_level} />}{' '}
        <span>{execution.business_error_description || 'No longer in the catalog'}</span>
      </div>

      <div className="mb-3">
        <strong>Delivery:</strong>
        <ul className="mb-0">
          {CHANNELS.map((channel) => {
            const delivery = channelDelivery(execution, channel)

            return delivery.recipients.length > 0 && <li key={channel}>{deliveryDescription(channel, delivery)}</li>
          })}
          {nobodyNotified(execution) && <li>Nobody was notified — no channel was enabled with recipients.</li>}
        </ul>
      </div>

      <div>
        <strong>Occurrences that triggered it</strong>
        <div className="mt-2">{renderOccurrences()}</div>
      </div>
    </Modal>
  )
}

export default ExecutionDetailsModal
